/**
 * Extracts user-supplied `{{variable}}` placeholders from an agent system
 * prompt — excluding system-resolved names (`customer_number`,
 * `assistant_number`, `current_time_*`).
 *
 * This is the LEGACY path, kept byte-identical on purpose: `analyzePrompt` in
 * `prompt-template.ts` falls back to it for any prompt containing no `{% %}`
 * tag, which is what guarantees existing agents behave exactly as they did
 * before Liquid conditionals existed. Call `analyzePrompt` instead of this
 * directly — it also tells required variables from optional ones.
 *
 * (There used to be a copy of this in the React app and another inlined in the
 * campaign dialog. They had already drifted apart; both are gone.)
 */

const SYSTEM_VARIABLES = new Set(["customer_number", "assistant_number"]);
const VARIABLE_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

export function extractDynamicVariables(systemPrompt: string): string[] {
  if (!systemPrompt) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const match of systemPrompt.matchAll(VARIABLE_PATTERN)) {
    const name = match[1];
    if (!name) continue;
    if (SYSTEM_VARIABLES.has(name)) continue;
    if (name.startsWith("current_time_")) continue;
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

/**
 * Sanitize a `{ key: value }` map of visitor-supplied dynamic variables.
 * - drops `__`-prefixed keys (internal namespace)
 * - drops non-string keys, null/object/array values
 * - coerces primitives to string
 * - caps key length (64) and value length (500)
 * - optionally restricts to an allowlist of required keys (returns
 *   `{ ok: false, unknown }` if a key isn't in the allowlist)
 */
export function sanitizeVisitorDynamicVariables(
  raw: unknown,
  allowlist?: string[],
):
  | { ok: true; values: Record<string, string> }
  | { ok: false; reason: "invalid_shape" | "unknown_keys"; unknown?: string[] } {
  if (raw === undefined || raw === null) return { ok: true, values: {} };
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, reason: "invalid_shape" };
  }

  const allow = allowlist ? new Set(allowlist) : null;
  const unknown: string[] = [];
  const values: Record<string, string> = {};

  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof key !== "string" || !key) continue;
    if (key.startsWith("__")) continue;
    if (key.length > 64) continue;
    if (value === null || value === undefined) continue;
    if (typeof value === "object") continue;
    if (allow && !allow.has(key)) {
      unknown.push(key);
      continue;
    }
    const str = String(value);
    values[key] = str.length > 500 ? str.slice(0, 500) : str;
  }

  if (unknown.length > 0) {
    return { ok: false, reason: "unknown_keys", unknown };
  }
  return { ok: true, values };
}
